import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  STEELMAN,
  INTERNAL,
  WHY,
  CODE,
  CONTEXT,
  LAYERS,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK : the contrarian thesis, "dead" struck through */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const strike = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="opinion · mcp vs cli" color={COLORS.cyan} />
          <Big>
            Everyone says MCP is{" "}
            <span style={{ position: "relative", color: COLORS.red }}>
              dead
              <span
                style={{
                  position: "absolute",
                  left: -6,
                  right: -6,
                  top: "52%",
                  height: 8,
                  background: COLORS.red,
                  transform: `scaleX(${strike})`,
                  transformOrigin: "left",
                }}
              />
            </span>
            <br />
            <span style={{ color: COLORS.cyan }}>They're wrong.</span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — STEELMAN : the three valid criticisms */
export const Scene02Steelman: React.FC = () => {
  const frame = useCurrentFrame();
  const rows = [
    { t: "tool schemas add tokens", at: 14 },
    { t: "auth adds friction", at: 34 },
    { t: "agents already live in the terminal", at: 54 },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.steelman.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="steelman the critics" color={COLORS.amber} />
          <Panel accent="amber" title="why people say MCP lost" width={1080} padding={26}>
            <div style={{ fontFamily: FONTS.body, fontSize: 34, lineHeight: 2.0, color: COLORS.text }}>
              {rows.map((r, i) => {
                const o = interpolate(frame, [r.at, r.at + 16], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });
                return (
                  <div key={i} style={{ opacity: o, display: "flex", gap: 16, alignItems: "baseline" }}>
                    <span style={{ color: COLORS.amber, fontFamily: FONTS.mono, fontSize: 26 }}>✓</span>
                    <span>{r.t}</span>
                  </div>
                );
              })}
            </div>
          </Panel>
          <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 26, maxWidth: 1040, textAlign: "center" }}>
            all true — <span style={{ color: COLORS.text }}>about MCP as internal plumbing</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={STEELMAN} accent="amber" />
    </AbsoluteFill>
  );
};

/* 03 — INTERNAL : the RAG pipeline is all direct imports, NO MCP */
const RAG_PIPE: PipeNode[] = [
  { label: "expand", value: "query-expander", accent: "cyan" },
  { label: "rewrite", value: "query-rewriter", accent: "green" },
  { label: "decompose", value: "query-decomposer", accent: "lime" },
  { label: "search", value: "retrieval", sub: "pgvector + bm25", accent: "amber" },
  { label: "rerank", value: "reranker", accent: "magenta" },
];
export const Scene03Internal: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.internal.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
      <Kicker text="inside eakc · src/lib/rag/" color={COLORS.green} />
      <div style={{ width: 1760 }}>
        <PipelineGraph nodes={RAG_PIPE} startFrame={8} stepFrames={20} nodeWidth={300} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 26,
          color: COLORS.dim,
          marginTop: 46,
          letterSpacing: "0.04em",
        }}
      >
        every arrow is an{" "}
        <span style={{ color: COLORS.text }}>import</span> — <span style={{ color: COLORS.red }}>0</span> MCP
        calls
      </div>
    </AbsoluteFill>
    <Caption lines={INTERNAL} accent="green" />
  </AbsoluteFill>
);

/* 04 — WHY : the server exists for OTHER agents */
export const Scene04Why: React.FC = () => {
  const frame = useCurrentFrame();
  const clients = ["Claude Desktop", "Cursor", "custom agent"];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.why.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="why ship the server at all" color={COLORS.violet} />
          <div style={{ display: "flex", gap: 22, marginBottom: 38 }}>
            {clients.map((c, i) => {
              const o = interpolate(frame, [18 + i * 14, 18 + i * 14 + 14], [0, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              });
              return (
                <div key={c} style={{ opacity: o }}>
                  <Panel accent="violet" width={300}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text, textAlign: "center" }}>
                      {c}
                    </div>
                  </Panel>
                </div>
              );
            })}
          </div>
          <div style={{ fontFamily: FONTS.display, fontSize: 46, color: COLORS.dim }}>↓ one contract ↓</div>
          <div style={{ marginTop: 20 }}>
            <Panel accent="cyan" title="eakc-mcp · /api/mcp" width={620}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 40, color: COLORS.cyan, textAlign: "center" }}>
                standardized discovery
              </div>
            </Panel>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={WHY} accent="violet" />
    </AbsoluteFill>
  );
};

/* 05 — CODE : the real JSON-RPC server */
const ROUTE = `// src/app/api/mcp/route.ts — JSON-RPC 2.0 over HTTP
export async function POST(req: NextRequest) {
  const apiKey = req.headers.get('x-api-key')       // → tenant
  const auth   = await validateMcpApiKey(apiKey)
  checkRateLimit(\`mcp_key:\${hash(apiKey)}\`, {     // 60 / min
    limit: 60, windowMs: 60_000 })

  switch (method) {
    case 'tools/list':                              // ← discovery
      return jsonRpcResult(id, { tools: ALL_MCP_TOOLS })
    case 'tools/call':
      // query_eakc · list_sources · get_document …
      // every tool scoped to auth.tenantId
  }
}`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 36 }}>
      <CodePanel
        code={ROUTE}
        language="tsx"
        filename="src/app/api/mcp/route.ts"
        highlightLines={[4, 5, 6, 11]}
        revealFrames={280}
        fontSize={26}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="cyan" />
  </AbsoluteFill>
);

/* 06 — CONTEXT : the token-bloat argument, in proportion */
export const Scene06Context: React.FC = () => {
  const frame = useCurrentFrame();
  const grow = interpolate(frame, [20, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.context.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="'it bloats the context window'" color={COLORS.amber} />
          <Big size={64}>A few tool schemas</Big>
          <Big size={64}>
            vs a <span style={{ color: COLORS.cyan }}>1,000,000</span>-token window
          </Big>
          {/* proportion bar */}
          <div
            style={{
              width: 1200,
              height: 34,
              marginTop: 44,
              background: COLORS.panel,
              border: `1px solid ${COLORS.line}`,
              borderRadius: 8,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: `${grow * 1.2}%`,
                minWidth: 6,
                background: COLORS.amber,
              }}
            />
          </div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 16 }}>
            a sliver — and it's a config choice, not a protocol flaw
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={CONTEXT} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 — LAYERS : the three-layer model */
export const Scene07Layers: React.FC = () => {
  const frame = useCurrentFrame();
  const layers = [
    { n: "01 · internal", v: "direct calls", sub: "fastest · no overhead", c: COLORS.green },
    { n: "02 · dev tools", v: "CLI", sub: "familiar · composable", c: COLORS.cyan },
    { n: "03 · external", v: "protocol (MCP)", sub: "standardized · discoverable", c: COLORS.violet },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.layers.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="it was never one-vs-the-other" color={COLORS.lime} />
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {layers.map((l, i) => {
            const o = interpolate(frame, [16 + i * 18, 16 + i * 18 + 16], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={l.n}
                style={{
                  opacity: o,
                  transform: `translateX(${(1 - o) * -24}px)`,
                  display: "flex",
                  alignItems: "center",
                  gap: 26,
                  width: 1180,
                  background: COLORS.panel,
                  border: `1px solid ${l.c}66`,
                  borderLeft: `5px solid ${l.c}`,
                  borderRadius: 10,
                  padding: "20px 30px",
                }}
              >
                <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: l.c, width: 220 }}>{l.n}</div>
                <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 40, color: COLORS.text, flex: 1 }}>
                  {l.v}
                </div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim }}>{l.sub}</div>
              </div>
            );
          })}
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 30 }}>
          the hot take compares <span style={{ color: COLORS.cyan }}>layer 2</span> with{" "}
          <span style={{ color: COLORS.violet }}>layer 3</span>
        </div>
      </AbsoluteFill>
      <Caption lines={LAYERS} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          If it's you calling → <span style={{ color: COLORS.cyan }}>CLI</span>.
          <br />
          If it's other agents → <span style={{ color: COLORS.violet }}>a protocol</span>.
        </Big>
        <div style={{ marginTop: 28 }}>
          <Callout label="the question that actually matters" accent="green" width={760}>
            not "MCP or CLI" — <b style={{ color: COLORS.text }}>who is calling your system?</b>
          </Callout>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 22, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 40 }}>
          EAKC · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
